import React, { useMemo, useEffect, useCallback, useRef } from 'react'
import { useLocation } from 'react-router'

import {
  usePRFileArtboards,
  usePRFileSections,
  usePan,
  useRootNodeAbsoluteExtent,
  useSetPan,
  useZoom,
} from '@sketch-hq/sketch-web-renderer'

import { useCanvasScope } from '../../scope'

import { AnnotationDotFragment } from '@sketch/gql-types'

import { AnnotationOverlay } from '../../../annotations/components'
import { usePlaceDraftAnnotation } from '../../../annotations/hooks'
import {
  ArtboardContainerBounds,
  ReferenceBounds,
} from '../../../annotations/types'

interface CenteredDotLocationState {
  isFromCommentTab?: boolean
}

interface AnnotationsOverlayProps {
  parentWrapper: React.RefObject<HTMLElement | null>
  activeAnnotationIdentifier?: string
}

const DEFAULT_PAN = { x: 0, y: 0 }

const throttleUpdatePopover = () => {
  window.requestAnimationFrame(() => {
    /**
     * This is a work-around to force popper to update
     * its position.
     *
     * Because popper has listeners for scroll and window
     * resize, we send this fake event when the camera is moving
     *
     * The other way would be using refs and would make the
     * logic complex
     */
    window?.dispatchEvent(new CustomEvent('scroll'))
  })
}

export const AnnotationsOverlay = (props: AnnotationsOverlayProps) => {
  const { activeAnnotationIdentifier, ...otherProps } = props

  const location = useLocation<CenteredDotLocationState | undefined>()
  const artboards = usePRFileArtboards()
  const sections = usePRFileSections()
  const setPan = useSetPan()

  const [isPlacingDraftAnnotation] = usePlaceDraftAnnotation() || []
  const rootExtent = useRootNodeAbsoluteExtent()

  const artboardCoordinatesByUuid = useMemo(() => {
    const coordinatesByUuid: Record<string, ReferenceBounds> = {}

    artboards?.forEach(artboard => {
      coordinatesByUuid[artboard.objectId] = artboard.bounds
    })

    return coordinatesByUuid
  }, [artboards])

  const sectionCoordinatesByUuid = useMemo(() => {
    const coordinatesByUuid: Record<string, ReferenceBounds> = {}

    sections?.forEach(section => {
      coordinatesByUuid[section.objectId] = section.bounds
    })

    return coordinatesByUuid
  }, [sections])

  const getPrefixCoordinates = useCallback(
    (annotation: AnnotationDotFragment) => {
      if (annotation.currentSubject.__typename === 'Artboard') {
        return artboardCoordinatesByUuid[annotation.currentSubject.uuid]
      }

      if (annotation.sectionUuid) {
        return sectionCoordinatesByUuid[annotation.sectionUuid]
      }
    },
    [artboardCoordinatesByUuid, sectionCoordinatesByUuid]
  )

  const getArtboardContainerBounds = useFocusedFrameContainerBounds(
    artboardCoordinatesByUuid,
    otherProps.parentWrapper
  )

  /**
   * When the user is panning on the canvas he might reach any
   * of the borders, if possible the popover should move to the
   * opposite side until reaching the other edge.
   */
  const pan = usePan() || DEFAULT_PAN
  useEffect(() => {
    /**
     * We should only activate this when the camera is moving
     * because when zooming we already have the "DROPDOWN_MODIFIERS"
     * that should keep the popover close to the dot
     */
    if (!(activeAnnotationIdentifier || isPlacingDraftAnnotation)) {
      return
    }

    throttleUpdatePopover()
  }, [pan.x, pan.y, activeAnnotationIdentifier, isPlacingDraftAnnotation])

  const handleActiveAnnotation = useCallback(
    (annotation: AnnotationDotFragment) => {
      const { coordinates } = annotation

      if (!coordinates || !location.state?.isFromCommentTab) {
        return
      }

      const prefixBounds = getPrefixCoordinates(annotation)

      const adjustmentX = rootExtent.x - (prefixBounds?.x || 0)
      const adjustmentY = rootExtent.y - (prefixBounds?.y || 0)

      const panCoordinates = {
        x: -(coordinates.x - adjustmentX),
        y: -(coordinates.y - adjustmentY),
      }

      setPan({
        ...panCoordinates,
        centerPointInViewport: true,
      })
    },
    [getPrefixCoordinates, setPan, location, rootExtent]
  )

  return (
    <AnnotationOverlay
      referenceBounds={rootExtent}
      getPrefixCoordinates={getPrefixCoordinates}
      getArtboardBounds={getPrefixCoordinates}
      getArtboardContainerBounds={getArtboardContainerBounds}
      onActiveAnnotation={handleActiveAnnotation}
      {...otherProps}
    />
  )
}

/**
 * Confines the annotation popover to the focused frame's rectangle on screen.
 *
 * Only meaningful in a frame focus view - on the page canvas, and in a Section
 * focus view, there is no single entity to confine it to and the popover is
 * free to sit anywhere over the canvas.
 *
 * Pan and zoom are read through refs: the popover is only repositioned when the
 * camera settles, and reading them directly would rebuild this callback on
 * every frame of a drag.
 */
function useFocusedFrameContainerBounds(
  artboardCoordinatesByUuid: Record<string, ReferenceBounds>,
  parentWrapper: React.RefObject<HTMLElement | null>
) {
  const scope = useCanvasScope()
  const focusedFrameUUID =
    scope?.focus.kind === 'frame' ? scope.focus.uuid : undefined

  const rootExtent = useRootNodeAbsoluteExtent()
  const scale = useZoom() || 1
  const pan = usePan()

  const cachedPan = useRef(pan)
  useEffect(() => {
    cachedPan.current = pan
  }, [pan])

  const cachedScale = useRef(scale)
  useEffect(() => {
    cachedScale.current = scale
  }, [scale])

  return useCallback((): ArtboardContainerBounds | undefined => {
    if (!focusedFrameUUID) return undefined

    const frameBounds = artboardCoordinatesByUuid[focusedFrameUUID]

    if (!frameBounds || !rootExtent || !cachedPan.current) return undefined
    if (!parentWrapper.current) return undefined

    const parentBounds = parentWrapper.current.getBoundingClientRect()

    const top =
      (cachedPan.current.y + frameBounds.y - rootExtent.y) *
        cachedScale.current +
      parentBounds.y
    const left =
      (cachedPan.current.x + frameBounds.x - rootExtent.x) *
        cachedScale.current +
      parentBounds.x

    return {
      top,
      bottom: top + frameBounds.height * cachedScale.current,
      left,
      right: left + frameBounds.width * cachedScale.current,
    }
  }, [focusedFrameUUID, artboardCoordinatesByUuid, rootExtent, parentWrapper])
}
